// frontend/src/pages/ServiceTypeManagement.tsx
// FINAL: Mobile-first refactored implementation with unified form

import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  useServiceTypes,
  usePendingServiceTypes,
  useCreateServiceType,
  useUpdateServiceType,
  useDeleteServiceType,
  useToggleServiceTypeStatus,
  useApproveServiceType,
  useRejectServiceType,
} from '@/hooks/useServiceTypes';
import { useCategories } from '@/hooks/useCategories';
import { ServiceTypeFilters, ServiceTypeFormData } from '@/lib/api/serviceTypeService';

// Import mobile-first components
import ServiceTypeListMobile from '@/components/ServiceType/ServiceTypeListMobile';
import ServiceTypeEmptyState from '@/components/ServiceType/ServiceTypeEmptyState';
import ServiceTypeForm from '@/components/ServiceType/ServiceTypeForm';
import { LoadingState, ErrorState, Pagination, PageHeader, PageFilters, useFilters, StatsGrid, StatItem, FilterPanel } from '@/components/common/ui';
import type { FilterField } from '@/components/common/ui/PageFilters';
import { Settings, Plus, Clock, RefreshCw, CheckCircle, X } from 'lucide-react';

type ViewMode = 'list' | 'form';

interface ServiceTypeFormState extends ServiceTypeFormData {
  service_type_id?: number;
}

const ServiceTypeManagement: React.FC = () => {
  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingServiceType, setEditingServiceType] = useState<any>(null);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Filter state using useFilters hook
  const {
    filters: filterValues,
    updateFilter,
    clearFilters: clearFilterValues,
    hasActiveFilters
  } = useFilters({
    search: '',
    category: '',
    status: '',
    approval: ''
  });

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo<ServiceTypeFilters>(() => ({
    page,
    limit,
    ...(filterValues.search && { search: filterValues.search }),
    ...(filterValues.category && { category: filterValues.category }),
    ...(filterValues.status && { status: filterValues.status }),
    ...(filterValues.approval && { approval: filterValues.approval })
  }), [page, limit, filterValues.search, filterValues.category, filterValues.status, filterValues.approval]);

  // React Query hooks
  const { 
    data: serviceTypesData, 
    isLoading, 
    error, 
    refetch 
  } = useServiceTypes(filters);

  const { 
    data: pendingData,
    isLoading: isLoadingPending
  } = usePendingServiceTypes(filters);

  const { 
    data: categoriesData,
    isLoading: isLoadingCategories 
  } = useCategories();

  // Mutation hooks
  const createMutation = useCreateServiceType();
  const updateMutation = useUpdateServiceType();
  const deleteMutation = useDeleteServiceType();
  const toggleStatusMutation = useToggleServiceTypeStatus();
  const approveMutation = useApproveServiceType();
  const rejectMutation = useRejectServiceType();

  // Computed values
  const displayData = showPendingOnly ? pendingData : serviceTypesData;
  const serviceTypes = displayData?.service_types || [];
  const stats = serviceTypesData?.stats || { total: 0, active: 0, pending: 0, inactive: 0 };
  const categories = categoriesData?.categories || [];
  const pendingCount = pendingData?.service_types?.length || 0;

  // Filter fields configuration
  const filterFields = useMemo<FilterField[]>(() => [
    {
      key: 'search',
      label: 'Search Service Types',
      type: 'search',
      placeholder: 'Search service types...',
      width: 'lg',
      clearable: true
    },
    {
      key: 'category',
      label: 'Category',
      type: 'select',
      width: 'md',
      options: categories.map(cat => ({
        label: cat.name,
        value: cat.category_id.toString()
      }))
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ]
    },
    {
      key: 'approval',
      label: 'Approval',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' }
      ]
    }
  ], [categories]);

  // Event handlers
  const handleFilterChange = useCallback((key: string, value: any) => {
    updateFilter(key, value);
    setPage(1);
  }, [updateFilter]);

  const handleClearFilters = useCallback(() => {
    clearFilterValues();
    setPage(1);
  }, [clearFilterValues]);

  const handleCreateNew = useCallback(() => {
    setEditingServiceType(null);
    setViewMode('form');
  }, []);

  const handleEdit = useCallback((serviceType: any) => {
    setEditingServiceType(serviceType);
    setViewMode('form');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setEditingServiceType(null);
  }, []);

  const handleFormSubmit = async (data: ServiceTypeFormData) => {
    try {
      if (editingServiceType) {
        await updateMutation.mutateAsync({
          id: editingServiceType.service_type_id,
          data
        });
        toast.success('Service type updated successfully');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Service type created successfully');
      }
      handleBackToList();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save service type');
      throw error; // Re-throw so form can handle it
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Service type deleted successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete service type');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await toggleStatusMutation.mutateAsync(id);
      toast.success('Status updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await approveMutation.mutateAsync(id);
      toast.success('Service type approved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve service type');
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm('Are you sure you want to reject this service type?')) {
      return;
    }

    try {
      await rejectMutation.mutateAsync(id);
      toast.success('Service type rejected successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject service type');
    }
  };




  const handleTogglePending = () => {
    setShowPendingOnly(!showPendingOnly);
    setPage(1);
  };

  const handleRefresh = () => {
    refetch();
  };

  // Form view
  if (viewMode === 'form') {
    return (
      <ServiceTypeForm
        serviceType={editingServiceType}
        categories={categories}
        isLoading={isLoadingCategories}
        onSubmit={handleFormSubmit}
        onCancel={handleBackToList}
      />
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Simple Page Title - Just text, no card */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Service Types</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage service type categories and approval workflows
          </p>
        </div>

        {/* Stats as Info Section - No card wrapper */}
        {stats && (
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
            <StatsGrid
              stats={[
                {
                  label: 'Total Service Types',
                  value: stats.total || 0,
                  icon: Settings,
                  color: 'gray'
                },
                {
                  label: 'Active',
                  value: stats.active || 0,
                  icon: CheckCircle,
                  color: 'green'
                },
                {
                  label: 'Pending',
                  value: stats.pending || 0,
                  icon: Clock,
                  color: 'yellow'
                },
                {
                  label: 'Inactive',
                  value: stats.inactive || 0,
                  icon: X,
                  color: 'red'
                }
              ]}
              layout="detailed"
              columns={{ mobile: 2, tablet: 2, desktop: 4 }}
            />
          </div>
        )}

        {/* Filters Section - Outside card */}
        <div className="mb-6">
          <FilterPanel
            fields={filterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
            variant="default"
            title="Service Type Filters"
            defaultExpanded={hasActiveFilters}
          />
        </div>

        {/* Actions - Below filters, above listing */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Service Type
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading || isLoadingPending}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${(isLoading || isLoadingPending) ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleTogglePending}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
                showPendingOnly 
                  ? 'text-white bg-yellow-600 border-yellow-600 hover:bg-yellow-700' 
                  : 'text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <Clock className="h-4 w-4 mr-2" />
              Pending ({pendingCount})
            </button>
          </div>
        </div>

        {/* Mobile: No card wrapper for max space */}
        <div className="block xl:hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Service Types</h3>
            {displayData && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {displayData.pagination?.total || serviceTypes?.length || 0} total
              </span>
            )}
          </div>
          {isLoading || isLoadingPending ? (
            <LoadingState
              variant="skeleton"
              title="Loading service types..."
              message="Please wait while we fetch your service types"
              size="md"
            />
          ) : error ? (
            <ErrorState
              type="server"
              title="Error loading service types"
              message={error.message || 'Failed to load service types'}
              onRetry={handleRefresh}
              size="md"
            />
          ) : serviceTypes.length === 0 ? (
            <ServiceTypeEmptyState
              showPendingOnly={showPendingOnly}
              onCreateNew={handleCreateNew}
            />
          ) : (
            <ServiceTypeListMobile
              serviceTypes={serviceTypes}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onApprove={showPendingOnly ? handleApprove : undefined}
              onReject={showPendingOnly ? handleReject : undefined}
            />
          )}
        </div>

        {/* Desktop: Card wrapper for clean look */}
        <div className="hidden xl:block bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Card Header with count */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Service Types</h3>
              {displayData && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {displayData.pagination?.total || serviceTypes?.length || 0} total
                </span>
              )}
            </div>
          </div>

          {/* Service Types Listing */}
          <div className="p-6">
            {isLoading || isLoadingPending ? (
              <LoadingState
                variant="skeleton"
                title="Loading service types..."
                message="Please wait while we fetch your service types"
                size="md"
              />
            ) : error ? (
              <ErrorState
                type="server"
                title="Error loading service types"
                message={error.message || 'Failed to load service types'}
                onRetry={handleRefresh}
                size="md"
              />
            ) : serviceTypes.length === 0 ? (
              <ServiceTypeEmptyState
                showPendingOnly={showPendingOnly}
                onCreateNew={handleCreateNew}
              />
            ) : (
              <ServiceTypeListMobile
                serviceTypes={serviceTypes}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                onApprove={showPendingOnly ? handleApprove : undefined}
                onReject={showPendingOnly ? handleReject : undefined}
              />
            )}
          </div>
        </div>

        {/* Pagination */}
        {displayData?.pagination && (
          <div className="flex justify-center mt-6">
            <Pagination
              currentPage={displayData.pagination.current_page || page}
              totalPages={displayData.pagination.total_pages || 1}
              totalItems={displayData.pagination.total || 0}
              itemsPerPage={displayData.pagination.per_page || limit}
              onPageChange={setPage}
              variant="default"
              size="md"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceTypeManagement;